<template>
  <div class="mobile-style-page">
    <!-- View Mode: Categories Selection OR Courses Display -->

    <!-- ===== CATEGORIES VIEW (when no childCategory selected) ===== -->
    <div v-if="!selectedChildCategory" class="categories-view">
      <!-- Mobile Header -->
      <div class="mobile-header">
        <div class="header-content">
          <h1 class="header-title">
            {{ $t("menu.categories") || "التخصصات" }}
          </h1>
          <button class="cart-button" @click="navigateToCart">
            <i class="fas fa-shopping-cart"></i>
          </button>
        </div>
      </div>

      <!-- Categories Loading -->
      <div v-if="isLoadingCategories" class="loading-container">
        <div class="spinner"></div>
        <p>{{ $t("common.loading") || "جاري التحميل..." }}</p>
      </div>

      <!-- Categories List (Mobile-style vertical list) -->
      <div v-else class="categories-list">
        <div
          v-for="category in categories"
          :key="category.id"
          class="category-section"
        >
          <!-- Category Header -->
          <div class="category-header" @click="toggleCategory(category.id)">
            <div class="category-icon">
              <img src="/assets/img/acad-icon.png" alt="" class="icon-img" />
            </div>
            <div class="category-info">
              <h3 class="category-title">
                {{ category.title?.en || category.title }}
              </h3>
              <span class="subcategory-count">
                {{ category.subcategory?.length || 0 }}
                {{ $t("home.subcategories_title") || "تخصصات فرعية" }}
              </span>
            </div>
            <i
              class="fas fa-chevron-down expand-icon"
              :class="{ rotated: expandedCategories.includes(category.id) }"
            ></i>
          </div>

          <!-- Subcategories (Expandable) -->
          <div
            class="subcategories-container"
            :class="{ expanded: expandedCategories.includes(category.id) }"
          >
            <div
              v-for="subcategory in category.subcategory"
              :key="subcategory.id"
              class="subcategory-section"
            >
              <!-- Subcategory Header -->
              <div
                class="subcategory-header"
                @click="toggleSubcategory(subcategory.id)"
              >
                <span class="subcategory-title">{{ subcategory.title }}</span>
                <div class="subcategory-actions">
                  <span
                    v-if="subcategory.childcategory?.length > 0"
                    class="child-count-badge"
                  >
                    {{ subcategory.childcategory.length }}
                  </span>
                  <i
                    class="fas fa-chevron-down expand-icon small"
                    :class="{
                      rotated: expandedSubcategories.includes(subcategory.id),
                    }"
                  ></i>
                </div>
              </div>

              <!-- Child Categories (Expandable) -->
              <div
                class="childcategories-container"
                :class="{
                  expanded: expandedSubcategories.includes(subcategory.id),
                }"
              >
                <div
                  v-for="childCategory in subcategory.childcategory"
                  :key="childCategory.id"
                  class="childcategory-item"
                  @click="selectChildCategory(childCategory, subcategory)"
                >
                  <span class="childcategory-title">{{
                    childCategory.title
                  }}</span>
                  <i class="fas fa-arrow-left action-arrow"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="categories.length === 0" class="empty-state">
          <i class="fas fa-folder-open"></i>
          <p>{{ $t("category.no_categories") || "لا توجد تخصصات" }}</p>
        </div>
      </div>
    </div>

    <!-- ===== COURSES VIEW (when childCategory is selected) ===== -->
    <div v-else class="courses-view">
      <!-- Mobile Header with Back Button -->
      <div class="mobile-header">
        <div class="header-content">
          <button class="back-button" @click="goBackToCategories">
            <i class="fas fa-arrow-right"></i>
          </button>
          <h1 class="header-title">{{ selectedChildCategory.title }}</h1>
          <button class="cart-button" @click="navigateToCart">
            <i class="fas fa-shopping-cart"></i>
          </button>
        </div>
      </div>

      <!-- Tab Bar (Recorded / Live / Bundles) -->
      <div class="mobile-tabs">
        <button
          class="mobile-tab"
          :class="{ active: activeTab === 'recorded' }"
          @click="switchTab('recorded')"
        >
          {{ $t("category_courses.recorded_course") || "الدورات المسجلة" }}
        </button>
        <button
          class="mobile-tab"
          :class="{ active: activeTab === 'live' }"
          @click="switchTab('live')"
        >
          {{ $t("category_courses.live") || "البث المباشر" }}
        </button>
        <button
          class="mobile-tab"
          :class="{ active: activeTab === 'bundles' }"
          @click="switchTab('bundles')"
        >
          {{ $t("category_courses.all_bundles") || "جميع الباقات" }}
        </button>
      </div>

      <!-- Search Bar -->
      <div class="search-container">
        <div class="search-bar">
          <input
            v-model="searchText"
            type="text"
            class="search-input"
            :placeholder="$t('common.search') || 'بحث'"
            @keyup.enter="performSearch"
          />
          <button class="search-button" @click="performSearch">
            <i class="fas fa-search"></i>
            <span>{{ $t("common.search") || "بحث" }}</span>
          </button>
        </div>
      </div>

      <!-- Content Area -->
      <div class="content-area">
        <!-- Loading State -->
        <div v-if="isLoadingCourses" class="loading-container">
          <div class="spinner"></div>
          <p>{{ $t("common.loading") || "جاري التحميل..." }}</p>
        </div>

        <!-- Courses/Bundles List -->
        <div v-else>
          <!-- Empty State -->
          <div v-if="displayItems.length === 0" class="empty-state">
            <i
              :class="
                activeTab === 'bundles' ? 'fas fa-box-open' : 'fas fa-book-open'
              "
            ></i>
            <p>
              {{
                activeTab === "bundles"
                  ? $t("category_courses.no_bundles") || "لا توجد باقات"
                  : $t("category_courses.no_courses") || "لا توجد دورات"
              }}
            </p>
          </div>

          <!-- Items List -->
          <div v-else class="items-list">
            <div
              v-for="item in displayItems"
              :key="item.id"
              class="list-item-card"
              @click="navigateToItem(item)"
            >
              <!-- Image Section -->
              <div class="item-image-section">
                <div class="item-image-wrapper">
                  <img
                    :src="item.image || item.preview_image || defaultImage"
                    @error="setDefaultImage"
                    alt=""
                    class="item-image"
                  />
                  <!-- Cart Action Overlay -->
                  <div
                    v-if="!item.is_purchased && getItemPrice(item) > 0"
                    class="image-actions-overlay"
                  >
                    <button
                      class="cart-icon-btn"
                      :disabled="loadingCart[item.id]"
                      @click.stop="addToCart(item)"
                    >
                      <i
                        v-if="loadingCart[item.id]"
                        class="fas fa-spinner fa-spin"
                      ></i>
                      <i
                        v-else-if="isInCart(item)"
                        class="fas fa-shopping-cart"
                      ></i>
                      <i v-else class="fas fa-cart-plus"></i>
                    </button>
                  </div>
                </div>
                <!-- Last Update Hint -->
                <div
                  v-if="item.last_update && activeTab !== 'bundles'"
                  class="image-hint"
                >
                  {{ $t("category_courses.last_updated") || "آخر تحديث" }}
                  {{ formatDate(item.last_update) }}
                </div>
              </div>

              <!-- Content Section -->
              <div class="item-content-section">
                <!-- Title & Subtitle -->
                <div class="item-info">
                  <h3 class="item-title">{{ item.title }}</h3>
                  <p v-if="getInstructorName(item)" class="item-subtitle">
                    {{ getInstructorName(item) }}
                  </p>

                  <!-- Features/Badges -->
                  <div class="item-features">
                    <!-- Live Badge (Animated) -->
                    <span v-if="item.is_live === '1'" class="badge live-badge">
                      <span class="live-dot"></span>
                      {{ $t("category_courses.live") || "مباشر" }}
                    </span>
                    <!-- QBank Badge -->
                    <span
                      v-if="item.subscription_plan_id"
                      class="badge qbank-badge"
                    >
                      {{ $t("category_courses.qbank") || "بنك أسئلة" }}
                    </span>
                    <!-- Institute Badge -->
                    <span v-if="item.institute" class="badge institute-badge">
                      {{ item.institute.title || item.institute }}
                    </span>
                  </div>
                </div>

                <!-- Bottom Row: Progress/Price + Action -->
                <div class="item-bottom">
                  <!-- Progress Bar (for purchased courses) -->
                  <div v-if="item.is_purchased" class="progress-section">
                    <div class="progress-bar-container">
                      <div
                        class="progress-bar-fill"
                        :style="{ width: `${item.progress || 0}%` }"
                      ></div>
                    </div>
                    <span class="progress-text">{{ item.progress || 0 }}%</span>
                  </div>

                  <!-- Price Section -->
                  <div v-else class="price-section">
                    <!-- Free -->
                    <span v-if="isFree(item)" class="price-free">
                      {{ $t("course.free") || "مجاني" }}
                    </span>
                    <!-- Discounted -->
                    <template v-else-if="hasDiscount(item)">
                      <span class="price-old">{{ item.price }}</span>
                      <span class="price-current">
                        {{ item.discount_price }} {{ $appCurrency }}
                      </span>
                    </template>
                    <!-- Regular -->
                    <span v-else class="price-current">
                      {{ item.price }} {{ $appCurrency }}
                    </span>
                  </div>

                  <!-- Action Button -->
                  <button class="action-btn" @click.stop="navigateToItem(item)">
                    <i class="fas fa-arrow-left"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="totalPages > 1" class="pagination-container">
            <button
              class="pagination-btn"
              :disabled="currentPage === 1"
              @click="changePage(currentPage - 1)"
            >
              <i class="fas fa-chevron-right"></i>
            </button>
            <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
            <button
              class="pagination-btn"
              :disabled="currentPage === totalPages"
              @click="changePage(currentPage + 1)"
            >
              <i class="fas fa-chevron-left"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiUrl } from "@/utils/api";
import Swal from "sweetalert2";

export default {
  name: "MobileStyleCourses",
  data() {
    return {
      // Categories state
      categories: [],
      isLoadingCategories: true,
      expandedCategories: [],
      expandedSubcategories: [],

      // Selected category state
      selectedChildCategory: null,
      selectedSubcategory: null,

      // Courses/Bundles state
      activeTab: "recorded",
      searchText: "",
      currentSearchQuery: "",
      courses: [],
      bundles: [],
      isLoadingCourses: false,
      currentPage: 1,
      totalPages: 1,
      perPage: 10,

      // Cart state
      loadingCart: {},
      cartCourses: [],
      cartBundles: [],

      // Misc
      defaultImage: "/assets/img/acad-icon.png",
      token: localStorage.getItem("access_token"),
    };
  },
  computed: {
    displayItems() {
      return this.activeTab === "bundles" ? this.bundles : this.courses;
    },
  },
  mounted() {
    this.initializeFromRoute();
    this.fetchCategories();
    this.loadCartItems();
  },
  methods: {
    // ===== Initialization =====
    initializeFromRoute() {
      const urlParams = new URLSearchParams(window.location.search);
      const childCategoryId = urlParams.get("childcategory_id");
      const childCategoryTitle = urlParams.get("title");

      if (childCategoryId) {
        this.selectedChildCategory = {
          id: parseInt(childCategoryId),
          title: childCategoryTitle || "Courses",
        };
        this.fetchCourses();
      }
    },

    // ===== Categories Methods =====
    async fetchCategories() {
      this.isLoadingCategories = true;
      try {
        const response = await fetch(`${apiUrl}/home/web`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion?.phone_code || "",
          },
        });
        const data = await response.json();
        this.categories = data.allcategory || [];
      } catch (error) {
        console.error("Failed to fetch categories:", error);
        this.categories = [];
      } finally {
        this.isLoadingCategories = false;
      }
    },

    toggleCategory(categoryId) {
      const index = this.expandedCategories.indexOf(categoryId);
      if (index > -1) {
        this.expandedCategories.splice(index, 1);
      } else {
        this.expandedCategories.push(categoryId);
      }
    },

    toggleSubcategory(subcategoryId) {
      const index = this.expandedSubcategories.indexOf(subcategoryId);
      if (index > -1) {
        this.expandedSubcategories.splice(index, 1);
      } else {
        this.expandedSubcategories.push(subcategoryId);
      }
    },

    selectChildCategory(childCategory, subcategory) {
      this.selectedChildCategory = childCategory;
      this.selectedSubcategory = subcategory;
      this.activeTab = "recorded";
      this.searchText = "";
      this.currentSearchQuery = "";
      this.currentPage = 1;

      // Update URL without reload
      const newUrl = `${window.location.pathname}?childcategory_id=${
        childCategory.id
      }&title=${encodeURIComponent(childCategory.title)}`;
      window.history.pushState({}, "", newUrl);

      this.fetchCourses();
    },

    goBackToCategories() {
      this.selectedChildCategory = null;
      this.selectedSubcategory = null;
      this.courses = [];
      this.bundles = [];

      // Update URL
      window.history.pushState({}, "", window.location.pathname);
    },

    // ===== Tab & Search Methods =====
    switchTab(tab) {
      this.activeTab = tab;
      this.searchText = "";
      this.currentSearchQuery = "";
      this.currentPage = 1;

      if (tab === "recorded") {
        this.fetchCourses(false);
      } else if (tab === "live") {
        this.fetchCourses(true);
      } else if (tab === "bundles") {
        this.fetchBundles();
      }
    },

    performSearch() {
      this.currentSearchQuery = this.searchText;
      this.currentPage = 1;

      if (this.activeTab === "bundles") {
        this.fetchBundles();
      } else {
        this.fetchCourses(this.activeTab === "live");
      }
    },

    // ===== Courses/Bundles Fetching =====
    async fetchCourses(isLive = false) {
      if (!this.selectedChildCategory) return;

      this.isLoadingCourses = true;

      const data = {
        page: this.currentPage,
        limit: this.perPage,
        childcategory_id: this.selectedChildCategory.id,
        is_live: isLive ? "1" : "0",
      };

      if (this.currentSearchQuery) {
        data.search = this.currentSearchQuery;
      }

      try {
        const response = await fetch(`${apiUrl}/web/get-courses`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion?.phone_code || "",
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify(data),
        });

        const result = await response.json();
        const resData = result.courses;

        if (resData && resData.data) {
          this.courses = resData.data;
          if (resData.meta) {
            this.totalPages = Math.ceil(
              resData.meta.total / resData.meta.per_page
            );
          }
        } else {
          this.courses = [];
          this.totalPages = 1;
        }
      } catch (error) {
        console.error("Error fetching courses:", error);
        this.courses = [];
        this.totalPages = 1;
      } finally {
        this.isLoadingCourses = false;
      }
    },

    async fetchBundles() {
      if (!this.selectedChildCategory) return;

      this.isLoadingCourses = true;

      const data = {
        page: this.currentPage,
        limit: this.perPage,
        childcategory_id: this.selectedChildCategory.id,
      };

      if (this.currentSearchQuery) {
        data.search = this.currentSearchQuery;
      }

      try {
        const response = await fetch(`${apiUrl}/web/get-bundles`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion?.phone_code || "",
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify(data),
        });

        const result = await response.json();
        const resData = result.bundle;

        if (resData && resData.data) {
          this.bundles = resData.data;
          if (resData.meta) {
            this.totalPages = Math.ceil(
              resData.meta.total / resData.meta.per_page
            );
          }
        } else if (Array.isArray(resData)) {
          this.bundles = resData;
          this.totalPages = 1;
        } else {
          this.bundles = [];
          this.totalPages = 1;
        }
      } catch (error) {
        console.error("Error fetching bundles:", error);
        this.bundles = [];
        this.totalPages = 1;
      } finally {
        this.isLoadingCourses = false;
      }
    },

    changePage(page) {
      this.currentPage = page;
      if (this.activeTab === "bundles") {
        this.fetchBundles();
      } else {
        this.fetchCourses(this.activeTab === "live");
      }
    },

    // ===== Cart Methods =====
    async loadCartItems() {
      if (!this.token) return;

      try {
        const response = await fetch(
          `${apiUrl}/show/cart?secret=a463c0cd-78ed-4bda-a5f6-f70c3e597dfb`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${this.token}`,
            },
          }
        );

        const data = await response.json();
        if (data.cart && Array.isArray(data.cart)) {
          this.cartCourses = data.cart
            .filter((item) => item.course_id !== null)
            .map((item) => item.course_id);
          this.cartBundles = data.cart
            .filter((item) => item.bundle_id !== null)
            .map((item) => item.bundle_id);
        }
      } catch (error) {
        console.error("Error loading cart:", error);
      }
    },

    async addToCart(item) {
      if (!this.token) {
        Swal.fire({
          icon: "warning",
          title: this.$t("common.login_required") || "يرجى تسجيل الدخول أولاً",
          confirmButtonText: this.$t("common.ok") || "حسناً",
        });
        return;
      }

      this.$set(this.loadingCart, item.id, true);

      const isBundle = this.activeTab === "bundles";
      const body = isBundle
        ? { bundle_id: item.id, secret: "a463c0cd-78ed-4bda-a5f6-f70c3e597dfb" }
        : {
            course_id: item.id,
            secret: "a463c0cd-78ed-4bda-a5f6-f70c3e597dfb",
          };

      try {
        const response = await fetch(`${apiUrl}/add/cart`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion?.phone_code || "",
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify(body),
        });

        const result = await response.json();

        if (response.ok) {
          if (isBundle) {
            this.cartBundles.push(item.id);
          } else {
            this.cartCourses.push(item.id);
          }
          Swal.fire({
            icon: "success",
            title: isBundle
              ? this.$t("category_courses.bundle_added_to_cart") ||
                "تمت إضافة الباقة للسلة"
              : this.$t("category_courses.course_added_to_cart") ||
                "تمت إضافة الدورة للسلة",
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 2000,
          });
        } else {
          const errorMsg = (result.message || "").toLowerCase();
          if (
            errorMsg.includes("already in cart") ||
            errorMsg.includes("already exists")
          ) {
            if (isBundle && !this.cartBundles.includes(item.id))
              this.cartBundles.push(item.id);
            else if (!isBundle && !this.cartCourses.includes(item.id))
              this.cartCourses.push(item.id);
            Swal.fire({
              icon: "info",
              title:
                this.$t("cart.already_in_cart") ||
                "هذا العنصر موجود بالفعل في سلتك",
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 2000,
            });
          } else {
            throw new Error(result.message || "Failed to add to cart");
          }
        }
      } catch (error) {
        console.error("Error adding to cart:", error);
        Swal.fire({
          icon: "error",
          title:
            this.$t("category_courses.cart_add_failed") ||
            "فشل في الإضافة للسلة",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2000,
        });
      } finally {
        this.$set(this.loadingCart, item.id, false);
      }
    },

    isInCart(item) {
      if (this.activeTab === "bundles") {
        return this.cartBundles.includes(item.id);
      }
      return this.cartCourses.includes(item.id);
    },

    // ===== Navigation Methods =====
    navigateToCart() {
      window.location.href = "/Cart";
    },

    navigateToItem(item) {
      if (this.activeTab === "bundles") {
        window.location.href = `/bundle-details?id=${item.id}`;
      } else {
        window.location.href = `/course-details?id=${item.id}`;
      }
    },

    // ===== Helper Methods =====
    getItemPrice(item) {
      return item.discount_price ?? item.price ?? 0;
    },

    getInstructorName(item) {
      if (item.user) {
        return `${item.user.fname || ""} ${item.user.lname || ""}`.trim();
      }
      return item.instructor_name || null;
    },

    isFree(item) {
      return item.discount_price === 0 || item.price === 0;
    },

    hasDiscount(item) {
      return item.discount_price != null && item.discount_price !== item.price;
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString(this.$i18n?.locale || "ar", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    setDefaultImage(event) {
      event.target.src = this.defaultImage;
    },
  },
};
</script>

<style scoped>
/* ===== Base Styles ===== */
.mobile-style-page {
  min-height: 100vh;
  background: #f5f5f5;
  direction: rtl;
}

/* ===== Mobile Header ===== */
.mobile-header {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  padding: 16px;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 800px;
  margin: 0 auto;
  gap: 12px;
}

.header-title {
  color: white;
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0;
  flex: 1;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.back-button,
.cart-button {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: background 0.3s ease;
}

.back-button:hover,
.cart-button:hover {
  background: rgba(255, 255, 255, 0.3);
}

.back-button i,
.cart-button i {
  font-size: 18px;
}

/* ===== Loading Container ===== */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e0e0e0;
  border-top: 4px solid #00c744;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-container p {
  margin-top: 16px;
  color: #666;
  font-weight: 600;
}

/* ===== Empty State ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.empty-state i {
  font-size: 60px;
  color: #ccc;
  margin-bottom: 16px;
}

.empty-state p {
  color: #666;
  font-size: 1rem;
  font-weight: 600;
}

/* ===== CATEGORIES VIEW STYLES ===== */
.categories-list {
  padding: 12px;
  max-width: 800px;
  margin: 0 auto;
}

.category-section {
  background: white;
  border-radius: 16px;
  margin-bottom: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.category-header {
  display: flex;
  align-items: center;
  padding: 16px;
  cursor: pointer;
  transition: background 0.3s ease;
  gap: 12px;
}

.category-header:hover {
  background: #f8f8f8;
}

.category-icon {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.icon-img {
  width: 30px;
  height: 30px;
  object-fit: contain;
}

.category-info {
  flex: 1;
  min-width: 0;
}

.category-title {
  font-size: 1rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 4px 0;
}

.subcategory-count {
  font-size: 0.8rem;
  color: #718096;
}

.expand-icon {
  color: #00c744;
  font-size: 14px;
  transition: transform 0.3s ease;
}

.expand-icon.rotated {
  transform: rotate(180deg);
}

.expand-icon.small {
  font-size: 12px;
}

/* Subcategories Container */
.subcategories-container {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.4s ease;
  background: #f8fffe;
}

.subcategories-container.expanded {
  max-height: 2000px;
}

.subcategory-section {
  border-top: 1px solid #e8fff1;
}

.subcategory-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 14px 24px;
  cursor: pointer;
  transition: background 0.3s ease;
}

.subcategory-header:hover {
  background: #f0fff7;
}

.subcategory-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2d3748;
}

.subcategory-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.child-count-badge {
  background: #00c744;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
}

/* Child Categories Container */
.childcategories-container {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.4s ease;
  background: white;
}

.childcategories-container.expanded {
  max-height: 1000px;
}

.childcategory-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 12px 36px;
  border-top: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.3s ease;
}

.childcategory-item:hover {
  background: #e8fff1;
}

.childcategory-title {
  font-size: 0.9rem;
  color: #4a5568;
}

.action-arrow {
  color: #00c744;
  font-size: 12px;
}

/* ===== COURSES VIEW STYLES ===== */

/* Tab Bar */
.mobile-tabs {
  display: flex;
  background: white;
  padding: 8px;
  gap: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow-x: auto;
  max-width: 800px;
  margin: 0 auto;
}

.mobile-tab {
  flex: 1;
  min-width: 100px;
  padding: 10px 12px;
  background: transparent;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.mobile-tab:hover {
  background: #f0f0f0;
  color: #00c744;
}

.mobile-tab.active {
  background: #00c744;
  color: white;
  box-shadow: 0 2px 8px rgba(0, 199, 68, 0.3);
}

/* Search Bar */
.search-container {
  padding: 8px 12px;
  background: white;
  max-width: 800px;
  margin: 0 auto;
}

.search-bar {
  display: flex;
  gap: 8px;
  align-items: center;
  background: #f5f5f5;
  border-radius: 10px;
  padding: 4px;
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 10px 12px;
  font-size: 0.9rem;
  outline: none;
}

.search-button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #00c744;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.3s ease;
}

.search-button:hover {
  background: #00a83b;
}

/* Content Area */
.content-area {
  padding: 8px 12px;
  max-width: 800px;
  margin: 0 auto;
}

/* Items List */
.items-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* List Item Card (matching Flutter ListItem1) */
.list-item-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  padding: 8px;
  display: flex;
  gap: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.list-item-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #00c744;
}

/* Image Section */
.item-image-section {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.item-image-wrapper {
  position: relative;
  width: 120px;
  height: 120px;
}

.item-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 16px;
}

/* Image Actions Overlay */
.image-actions-overlay {
  position: absolute;
  bottom: 4px;
  right: 4px;
  left: 4px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.cart-icon-btn {
  background: transparent;
  border: none;
  color: #00c744;
  font-size: 18px;
  cursor: pointer;
  padding: 4px 8px;
  transition: color 0.3s ease;
}

.cart-icon-btn:hover:not(:disabled) {
  color: #00a83b;
}

.cart-icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Image Hint */
.image-hint {
  font-size: 0.65rem;
  color: #999;
  text-align: center;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Content Section */
.item-content-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
}

.item-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.item-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.3;
}

.item-subtitle {
  font-size: 0.8rem;
  color: #666;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Features/Badges */
.item-features {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-top: 6px;
  overflow-x: auto;
  scrollbar-width: none;
}

.item-features::-webkit-scrollbar {
  display: none;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
}

.live-badge {
  background: #ef4444;
  color: white;
}

.live-dot {
  width: 6px;
  height: 6px;
  background: white;
  border-radius: 50%;
  animation: pulse-dot 1s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

.qbank-badge {
  background: #3b82f6;
  color: white;
}

.institute-badge {
  background: #e8fff1;
  color: #00a83b;
}

/* Bottom Section */
.item-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
}

/* Progress Section */
.progress-section {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-bar-container {
  flex: 1;
  height: 6px;
  background: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: #00c744;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 0.75rem;
  color: #666;
  font-weight: 600;
  min-width: 35px;
  text-align: left;
}

/* Price Section */
.price-section {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.price-free {
  font-size: 0.95rem;
  font-weight: 700;
  color: #00c744;
}

.price-current {
  font-size: 0.95rem;
  font-weight: 700;
  color: #00c744;
}

.price-old {
  font-size: 0.75rem;
  color: #999;
  text-decoration: line-through;
}

/* Action Button */
.action-btn {
  width: 32px;
  height: 32px;
  min-width: 32px;
  background: #e8fff1;
  color: #00c744;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.action-btn:hover {
  background: #00c744;
  color: white;
  transform: scale(1.1);
}

.action-btn i {
  font-size: 12px;
}

/* Pagination */
.pagination-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 20px;
  margin-top: 12px;
}

.pagination-btn {
  width: 40px;
  height: 40px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00c744;
  cursor: pointer;
  transition: all 0.3s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: #00c744;
  color: white;
  border-color: #00c744;
}

.pagination-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.page-info {
  font-size: 0.9rem;
  font-weight: 600;
  color: #666;
}

/* ===== Responsive ===== */
@media (max-width: 480px) {
  .item-image-wrapper {
    width: 100px;
    height: 100px;
  }

  .image-hint {
    max-width: 100px;
  }

  .item-title {
    font-size: 0.9rem;
  }

  .mobile-tab {
    font-size: 0.8rem;
    padding: 8px 10px;
    min-width: 80px;
  }

  .search-button span {
    display: none;
  }

  .search-button {
    padding: 8px 12px;
  }
}

/* ===== Dark Mode Support ===== */
[data-bs-theme="dark"] .mobile-style-page,
[data-theme="dark"] .mobile-style-page,
.dark .mobile-style-page {
  background: #1a1a1a;
}

[data-bs-theme="dark"] .category-section,
[data-theme="dark"] .category-section,
.dark .category-section,
[data-bs-theme="dark"] .list-item-card,
[data-theme="dark"] .list-item-card,
.dark .list-item-card {
  background: #2d2d2d;
  border-color: #3d3d3d;
}

[data-bs-theme="dark"] .category-header:hover,
[data-theme="dark"] .category-header:hover,
.dark .category-header:hover {
  background: #3d3d3d;
}

[data-bs-theme="dark"] .category-title,
[data-theme="dark"] .category-title,
.dark .category-title,
[data-bs-theme="dark"] .item-title,
[data-theme="dark"] .item-title,
.dark .item-title {
  color: #f5f5f5;
}

[data-bs-theme="dark"] .subcategory-count,
[data-theme="dark"] .subcategory-count,
.dark .subcategory-count,
[data-bs-theme="dark"] .item-subtitle,
[data-theme="dark"] .item-subtitle,
.dark .item-subtitle {
  color: #999;
}

[data-bs-theme="dark"] .subcategories-container,
[data-theme="dark"] .subcategories-container,
.dark .subcategories-container {
  background: #252525;
}

[data-bs-theme="dark"] .childcategories-container,
[data-theme="dark"] .childcategories-container,
.dark .childcategories-container {
  background: #2d2d2d;
}

[data-bs-theme="dark"] .childcategory-item:hover,
[data-theme="dark"] .childcategory-item:hover,
.dark .childcategory-item:hover {
  background: #3d3d3d;
}

[data-bs-theme="dark"] .mobile-tabs,
[data-theme="dark"] .mobile-tabs,
.dark .mobile-tabs,
[data-bs-theme="dark"] .search-container,
[data-theme="dark"] .search-container,
.dark .search-container {
  background: #2d2d2d;
}

[data-bs-theme="dark"] .mobile-tab,
[data-theme="dark"] .mobile-tab,
.dark .mobile-tab {
  color: #999;
}

[data-bs-theme="dark"] .mobile-tab:hover,
[data-theme="dark"] .mobile-tab:hover,
.dark .mobile-tab:hover {
  background: #3d3d3d;
}

[data-bs-theme="dark"] .search-bar,
[data-theme="dark"] .search-bar,
.dark .search-bar {
  background: #3d3d3d;
}

[data-bs-theme="dark"] .search-input,
[data-theme="dark"] .search-input,
.dark .search-input {
  color: white;
}

[data-bs-theme="dark"] .category-icon,
[data-theme="dark"] .category-icon,
.dark .category-icon {
  background: linear-gradient(135deg, #0f3d1c 0%, #1a5c2e 100%);
}

[data-bs-theme="dark"] .progress-bar-container,
[data-theme="dark"] .progress-bar-container,
.dark .progress-bar-container {
  background: #3d3d3d;
}

[data-bs-theme="dark"] .action-btn,
[data-theme="dark"] .action-btn,
.dark .action-btn {
  background: #3d3d3d;
}

[data-bs-theme="dark"] .pagination-btn,
[data-theme="dark"] .pagination-btn,
.dark .pagination-btn {
  background: #2d2d2d;
  border-color: #3d3d3d;
}

[data-bs-theme="dark"] .image-actions-overlay,
[data-theme="dark"] .image-actions-overlay,
.dark .image-actions-overlay {
  background: rgba(45, 45, 45, 0.85);
}

[data-bs-theme="dark"] .institute-badge,
[data-theme="dark"] .institute-badge,
.dark .institute-badge {
  background: #1a5c2e;
  color: #4ade80;
}
</style>
